import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Restaurant } from '../../core/model/restaurant';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useCriteria } from '../../state/CriteriaContext';
import { useAsync } from '../../state/useAsync';
import { UiStates } from '../../state/uiState';
import type { HomeCollection } from '../../data/repository/repositories';
import { RestaurantCard } from '../components/RestaurantCard';
import { SearchCriteriaBar } from '../components/SearchCriteriaBar';
import { EmptyState, SkeletonList, StateContainer } from '../components/StateContainer';
import { SearchIcon } from '../components/icons';

/**
 * Inicio: descubrimiento público.
 *
 * Nunca pide sesión. La autenticación se exige más tarde, al confirmar una reserva, que es la
 * única operación que de verdad la necesita.
 */
export function HomeScreen() {
  const navigate = useNavigate();
  const { criteria, setQuery } = useCriteria();
  const [draftQuery, setDraftQuery] = useState('');

  const openRestaurant = useCallback((id: string) => navigate(`/restaurante/${id}`), [navigate]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery(draftQuery);
    navigate('/buscar');
  };

  return (
    <div className="stack stack--loose">
      <section className="stack">
        <h1>{S.homeGreeting}</h1>
        <form className="row" onSubmit={submitSearch} role="search">
          <input
            className="field__control"
            style={{ flex: '1 1 220px' }}
            type="search"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder={S.homeSearchHint}
            aria-label={S.homeSearchHint}
          />
          <button type="submit" className="btn">
            <SearchIcon size={18} /> {S.searchTitle}
          </button>
        </form>
        <SearchCriteriaBar />
      </section>

      <Collection
        collection="recommended"
        title={S.homeSectionRecommended}
        locationId={criteria.location.kind === 'NAMED' ? criteria.location.id : null}
        onOpen={openRestaurant}
      />
      <Collection
        collection="nearby"
        title={S.homeSectionNearby}
        locationId={criteria.location.kind === 'NAMED' ? criteria.location.id : null}
        onOpen={openRestaurant}
      />
      <Collection
        collection="featured"
        title={S.homeSectionFeatured}
        locationId={criteria.location.kind === 'NAMED' ? criteria.location.id : null}
        onOpen={openRestaurant}
      />
    </div>
  );
}

function Collection({
  collection,
  title,
  locationId,
  onOpen,
}: {
  collection: HomeCollection;
  title: string;
  locationId: string | null;
  onOpen: (id: string) => void;
}) {
  const { restaurants } = useContainer();
  const { setLocation } = useCriteria();

  const { state, reload } = useAsync<Restaurant[], readonly Restaurant[]>(
    () => restaurants.getCollection(collection, locationId),
    [collection, locationId, restaurants],
    UiStates.fromList,
  );

  return (
    <section className="section">
      <div className="section__header">
        <h2>{title}</h2>
      </div>
      <StateContainer
        state={state}
        loading={<SkeletonList count={3} />}
        onRetry={reload}
        empty={
          <EmptyState
            icon="📍"
            title={S.homeEmpty}
            action={{
              label: S.homeEmptyAction,
              // Volver a la ciudad completa es la salida real cuando una zona no tiene nada,
              // en lugar de dejar al usuario ante una sección vacía sin acción.
              onClick: () => setLocation({ kind: 'NAMED', id: 'cdmx', label: 'Ciudad de México' }),
            }}
          />
        }
      >
        {(items) => (
          <div className="carousel">
            {items.map((restaurant) => (
              <RestaurantCard key={restaurant.id} restaurant={restaurant} onOpen={onOpen} />
            ))}
          </div>
        )}
      </StateContainer>
    </section>
  );
}
